import type { BarcodeFormat } from '@erp/shared';
import JsBarcode from 'jsbarcode';
import { useEffect, useRef, useState } from 'react';

/**
 * Live SVG preview of a barcode. Renders client-side via `jsbarcode` so
 * keystrokes in the form propagate instantly — no API round-trip.
 *
 * QR is intentionally not rendered (jsbarcode is 1D-only). When the
 * tenant picks `qr` we show a placeholder note; the actual QR encoding
 * happens at print time once the renderer ships.
 */
export interface BarcodePreviewProps {
  value: string;
  format: BarcodeFormat;
  className?: string;
}

// jsbarcode's CODE128 fallback handles any ASCII; the digit-only
// symbologies (EAN/UPC) throw on the wrong length, so we surface the
// error to the user instead of silently rendering nothing.
const JSBARCODE_FORMAT: Record<Exclude<BarcodeFormat, 'qr'>, string> = {
  code128: 'CODE128',
  ean13: 'EAN13',
  ean8: 'EAN8',
  upc_a: 'UPC',
};

export function BarcodePreview({ value, format, className }: BarcodePreviewProps): JSX.Element {
  const ref = useRef<SVGSVGElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (!value || !value.trim()) {
      setError(null);
      ref.current.innerHTML = '';
      return;
    }
    if (format === 'qr') {
      setError(null);
      ref.current.innerHTML = '';
      return;
    }
    try {
      JsBarcode(ref.current, value.trim(), {
        format: JSBARCODE_FORMAT[format],
        displayValue: true,
        fontSize: 14,
        height: 60,
        margin: 4,
      });
      setError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    }
  }, [value, format]);

  const isEmpty = !value || !value.trim();

  return (
    <div
      className={`flex min-h-[100px] items-center justify-center rounded-md border border-dashed border-border bg-bg-muted p-3 ${
        className ?? ''
      }`}
    >
      {isEmpty ? (
        <span className="text-xs text-fg-muted">No barcode set</span>
      ) : format === 'qr' ? (
        <span className="text-xs text-fg-muted">
          QR preview not available — value will be encoded at print time
        </span>
      ) : error ? (
        <span className="text-xs text-danger">{error}</span>
      ) : (
        // jsbarcode mutates this <svg> in-place on every effect run.
        <svg ref={ref} aria-label={`Barcode for ${value}`} />
      )}
    </div>
  );
}
